import type { SelectChangeEvent } from "@mui/material";
import {
  Button,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { logError } from "@entrilia/shared/logging";
import { reportingPeriodCaptionsMap } from "@entrilia/shared/utilities/enumCaptions";
import adminApi from "../../../../api/adminApi";
import type {
  DataCollectionRequestTemplateVersion,
  DataSubmissionConfiguration,
} from "../../../../api/types/dataCollectionTypes";
import InlinePropertyBox from "../../../common/InlinePropertyBox";
import DataRequestSetupPageContext from "./DataRequestSetupPageContext";
import DataRequestTemplateRecipientFields from "./DataRequestTemplateRecipientFields";
import type { EditDataRequestTemplateState } from "./editDataRequestTemplateState";
import {
  getInitialEditDataRequestTemplateState,
  validateEditDataRequestTemplate,
} from "./editDataRequestTemplateState";

interface Props {
  onClose: () => void;
  onCreated: (template: DataCollectionRequestTemplateVersion) => void;
  existingTemplateNames: string[];
}

const createDataRequestTemplateVersion = withErrorHandling(adminApi.createDataRequestTemplateVersion);

const getConfigurationSettingsProperties = (
  configuration: DataSubmissionConfiguration | undefined
): Array<[string, string]> => [
  ["Scenario", configuration?.isScenarioConfigurable ? "N/A (configurable)" : (configuration?.scenario ?? "")],
  ["Period", configuration?.reportingPeriod ? reportingPeriodCaptionsMap[configuration.reportingPeriod] : ""],
];

const CreateDataRequestTemplateDialog = ({ onClose, onCreated, existingTemplateNames }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { configurations, categories, objectDefinition } = useDefinedContext(DataRequestSetupPageContext);

  const [state, setState] = useState<EditDataRequestTemplateState>(getInitialEditDataRequestTemplateState);
  const [configurationId, setConfigurationId] = useState("");
  const [loading, setLoading] = useState(false);
  const [isNameTouched, setIsNameTouched] = useState(false);

  const validationResults = useMemo(
    () => validateEditDataRequestTemplate(state, existingTemplateNames),
    [state, existingTemplateNames]
  );

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({ ...prev, name: e.target.value }));
    setIsNameTouched(true);
  };

  const handleConfigurationChange = (e: SelectChangeEvent) => {
    setConfigurationId(e.target.value);
  };

  const handleRecipientFieldsChange = (changes: Pick<EditDataRequestTemplateState, "auditory" | "categoryId">) => {
    setState((prev) => ({ ...prev, ...changes }));
  };

  const handleSubmit = async () => {
    if (!state.auditory) {
      return;
    }

    setLoading(true);

    const [resp, error] = await createDataRequestTemplateVersion({
      objectType: objectDefinition.objectType,
      configurationId,
      name: state.name,
      auditory: state.auditory,
      objectAccessCategoryIds: [state.categoryId],
      recipientObjectIds: [],
    });

    setLoading(false);

    if (error) {
      logError(error, "[CreateDataRequestTemplateDialog] createDataRequestTemplateVersion");
      sendNotificationError(error.message);
      return;
    }

    if (!resp.success) {
      sendNotificationError("Could not create template:\n" + resp.errors.join("\n"));
      return;
    }

    sendNotification("Template created successfully");
    onCreated(resp.templateVersion);
  };

  const selectedConfiguration = configurations.find((c) => c.id === configurationId);

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>New Template</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2}>
          <FormControl fullWidth>
            <TextField
              value={state.name}
              onChange={handleNameChange}
              label="Name"
              error={isNameTouched && !validationResults.nameResult.isValid}
              helperText={isNameTouched && validationResults.nameResult.error}
            />
          </FormControl>

          <Stack spacing={1} sx={{ pt: 2 }}>
            <Typography variant="subtitle1">Form</Typography>
            <Typography color="textSecondary">
              Choose a pre-configured form to define metrics and structure of this request.
            </Typography>
          </Stack>

          <FormControl fullWidth>
            <InputLabel id="template-configuration-select-label">Configuration</InputLabel>
            <Select fullWidth label="Configuration" value={configurationId} onChange={handleConfigurationChange}>
              {configurations.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DataRequestTemplateRecipientFields
            auditory={state.auditory}
            categoryId={state.categoryId}
            categories={categories}
            objectDefinition={objectDefinition}
            onChange={handleRecipientFieldsChange}
          />

          <InlinePropertyBox
            title="Configuration Settings"
            properties={getConfigurationSettingsProperties(selectedConfiguration)}
          />
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="contained" loading={loading} onClick={handleSubmit} disabled={!validationResults.isValid}>
          Create
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default CreateDataRequestTemplateDialog;
